<template>
    <div class="fa-attr">
        <span class="fa-label">{{ value.label }}</span>
        <div class="fa-grid">
            <div v-for="file in files" :key="file.id" class="fa-card">
                <!-- Image preview on top -->
                <a
                    v-if="isImage(file.name) && !failed[file.id]"
                    class="fa-card-preview"
                    :href="fileUrl(file)"
                    target="_blank"
                    rel="noopener"
                    :title="$t('post.page.attachment.open')"
                >
                    <img
                        :src="fileUrl(file)"
                        :alt="file.name"
                        @error="onImageError(file)"
                    />
                </a>
                <!-- Preview unavailable (image that failed to load) -->
                <div v-else-if="isImage(file.name)" class="fa-card-icon-block fa-card-broken">
                    <i class="fal fa-eye-slash"></i>
                    <span>{{ $t('post.page.attachment.preview_unavailable') }}</span>
                </div>
                <!-- File icon for non-images -->
                <div v-else class="fa-card-icon-block">
                    <i :class="fileIcon(file.name)"></i>
                </div>
                <!-- Footer -->
                <div class="fa-card-footer">
                    <span class="fa-card-name" :title="file.name">{{ file.name }}</span>
                    <a
                        :href="downloadUrl(file)"
                        :download="file.name"
                        class="fa-card-dl"
                        :title="$t('post.page.attachment.download')"
                    >
                        <i class="fal fa-download text-white"></i>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
export default {
    name: 'FileUploadAttribute',
    props: ['value', 'medias'],
    data() {
        return {
            baseUrl: this.$store.state.baseURL,
            token: this.$store.state.auth.token,
            failed: {}
        }
    },
    computed: {
        // The answer holds media ids, but an edited answer may carry
        // objects ({ id, existing }) — accept both shapes.
        files() {
            const raw = this.value && this.value.value
            const ids = Array.isArray(raw) ? raw : raw ? [raw] : []
            return ids
                .map((entry) => this.getMedia(entry && entry.id ? entry.id : entry))
                .filter((media) => media && media.id)
        }
    },
    methods: {
        getMedia(id) {
            return (this.medias || []).filter((media) => media.id == id)[0]
        },
        fileUrl(media) {
            return `${this.baseUrl}/storage/${media.id}?token=${this.token}`
        },
        downloadUrl(media) {
            return `${this.baseUrl}/storage/download/${media.id}?token=${this.token}`
        },
        onImageError(media) {
            this.$set(this.failed, media.id, true)
        },
        isImage(name) {
            if (!name) return false
            const ext = name.split('.').pop().toLowerCase()
            return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
        },
        fileIcon(name) {
            if (!name) return 'fal fa-file'
            const ext = name.split('.').pop().toLowerCase()
            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'fal fa-file-image'
            if (['pdf'].includes(ext)) return 'fal fa-file-pdf'
            if (['doc', 'docx'].includes(ext)) return 'fal fa-file-word'
            if (['xls', 'xlsx'].includes(ext)) return 'fal fa-file-excel'
            if (['mp4', 'avi', 'mov'].includes(ext)) return 'fal fa-file-video'
            if (['mp3', 'wav', 'ogg', 'webm', 'm4a'].includes(ext)) return 'fal fa-file-audio'
            return 'fal fa-file'
        }
    }
}
</script>

<style scoped>
.fa-label {
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
    margin-bottom: 10px;
}

.fa-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
}

.fa-card {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.fa-card:hover {
    border-color: #bfdbfe;
    box-shadow: 0 2px 10px rgba(59, 130, 246, 0.08);
}

.fa-card-preview {
    display: block;
    height: 200px;
    overflow: hidden;
    background: #f1f5f9;
}

.fa-card-preview img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
}

.fa-card-icon-block {
    height: 120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: #f8fafc;
    font-size: 2rem;
    color: #94a3b8;
}

.fa-card-broken span {
    font-size: 0.72rem;
    font-style: italic;
}

.fa-card-footer {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-top: 1px solid #f1f5f9;
}

.fa-card-name {
    flex: 1;
    font-size: 0.78rem;
    font-weight: 600;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.fa-card-dl {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #0891b2;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.2s;
}

.fa-card-dl:hover {
    background: #0e7490;
}

.fa-card-dl i {
    color: #fff !important;
    font-size: 0.78rem;
}

@media (max-width: 640px) {
    .fa-grid {
        grid-template-columns: 1fr;
    }

    .fa-card-preview {
        height: 220px;
    }
}
</style>
